import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import { type FC, useEffect, useMemo, useRef, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useProductTechnicalInformation } from "../../../../Information/hooks/useProductTechnicalnformation"
import { useCreateProductTest, useUpdateProductTest } from "../../hooks"
import type { ProductTestFormValues } from "../../schemas"
import { createProductTestFormSchema } from "../../schemas"
import type { ProductTestDiffType, ProductTestFormPayload, ProductTestWithDocuments } from "../../types"
import {
  compareProductTestValues,
  getProductTestDefaultFormValues,
  hasProductTestDocumentsChanged,
  hasProductTestPoIdsChanged,
  toProductTestFormPayload,
} from "../../utils"
import { ProductTestDocumentsForm } from "../ProductTestDocumentsForm"
import { ProductTestVersionUpdateDialog } from "../ProductTestVersionUpdateDialog"

import { ProductTestCoaForm } from "./ProductTestCoaForm"
import { ProductTestCoaInfo } from "./ProductTestCoaInfo"
import { ProductTestExistsAlert } from "./ProductTestExistsAlert"
import { ProductTestInfo } from "./ProductTestInfo"
import { ProductTestResultsForm } from "./ProductTestResultsForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type { AssociatedTestTemplate, Product, ProductTest, SignedUrl } from "@/graphql/codegen/graphql"
import { useHasExistingProductTest } from "@/hooks/useHasExistingProductTest"
import { ProductTestLinkPoAlert } from "@/screens/Products/components/Product/Quality/TestResults/components/ProductTestFormDialog/ProductTestLinkPoAlert"
import { useCurrentUserStore } from "@/stores/currentUserStore"
import { toNumberOrNull } from "@/utils/forms"
import useDebounce from "@/utils/useDebounce"

enum ProductTestFormDialogType {
  Cancel = "CANCEL",
  Form = "FORM",
  VersionUpdate = "VERSION_UPDATE",
}

interface ProductTestFormDialogProps {
  product: Product
  associatedTestTemplates?: AssociatedTestTemplate[]
  productTest?: ProductTestWithDocuments | null
  isPoLinkMode?: boolean
  linkingOrderId?: string | null
  onPoLink: (productTest: ProductTest | null, orderId: string) => void
  onClose: () => void
  onSubmit: () => void
}

export const ProductTestFormDialog: FC<ProductTestFormDialogProps> = ({
  product,
  associatedTestTemplates = [],
  productTest = null,
  onPoLink,
  isPoLinkMode = false,
  linkingOrderId = "",
  onClose,
  onSubmit,
}) => {
  const isCreateMode = !productTest
  const initialProductTestDocuments = productTest?.documents ?? []
  const [productTestDocuments, setProductTestDocuments] = useState<SignedUrl[]>(initialProductTestDocuments)
  const [productTestPoIds, setProductTestPoIds] = useState<number[]>(
    productTest?.orders?.map((order) => order.orderId) ?? []
  )
  const [openedDialog, setOpenedDialog] = useState(ProductTestFormDialogType.Form)
  const [diffType, setDiffType] = useState<ProductTestDiffType | null>(null)
  const pendingFormPayloadRef = useRef<ProductTestFormPayload | null>(null)
  const currentUser = useCurrentUserStore((state) => state.currentUser)
  const methods = useForm<ProductTestFormValues>({
    defaultValues: getProductTestDefaultFormValues(associatedTestTemplates, productTest),
    resolver: zodResolver(createProductTestFormSchema(associatedTestTemplates)),
    mode: "all",
  })
  const {
    control,
    formState: { isValid, isDirty, dirtyFields },
    handleSubmit,
    trigger,
    watch,
  } = methods
  const { data: technicalInformation } = useProductTechnicalInformation({ productId: product.productId })
  const shelfLifeMonths = technicalInformation?.shelfLifeMonths ?? 0
  const lotNumber = watch("lotNumber")
  const batchNumber = watch("batchNumber")
  const orderId = watch("orderId")

  useEffect(() => {
    // Test result data received from AS400 may have incorrect formatting without trailing zeroes e.g. '1' instead of '1.00'.
    // Explicitly trigger validation in edit mode to ensure validation messages are displayed.
    if (!isCreateMode) {
      trigger()
    }
  }, [isCreateMode, trigger])

  const filter = useMemo(
    () => ({
      productId: product.productId,
      lotNumber,
      batchNumber,
    }),
    [product.productId, lotNumber, batchNumber]
  )
  const debouncedFilter = useDebounce(filter, DEBOUNCE_DELAY_MS)
  const {
    hasExisting: hasExistingProductTest,
    hasExistingToLinkPo: hasExistingProductTestToLinkPo,
    existingProductTest,
    isFetching: isFetchingProductTests,
  } = useHasExistingProductTest({
    filter: debouncedFilter,
    productTestId: productTest?.productTestId,
    orderId: toNumberOrNull(orderId),
  })
  const hasDocumentsChanged = hasProductTestDocumentsChanged(initialProductTestDocuments, productTestDocuments)
  const hasPoIdsChanged = hasProductTestPoIdsChanged(
    productTest?.orders?.map((order) => order.orderId) ?? [],
    productTestPoIds
  )
  const { createProductTest, isCreating } = useCreateProductTest()
  const { updateProductTest, isUpdating } = useUpdateProductTest()
  const isSubmitting = isCreating || isUpdating
  const canSubmit =
    isValid &&
    (isDirty || hasDocumentsChanged || hasPoIdsChanged) &&
    !isFetchingProductTests &&
    !hasExistingProductTest &&
    !isSubmitting

  const handlePoRemove = (removedPoId: number) => {
    setProductTestPoIds((prev) => prev.filter((poId) => poId !== removedPoId))
  }

  const handleClose = () => {
    if (isDirty || hasDocumentsChanged || hasPoIdsChanged) {
      setOpenedDialog(ProductTestFormDialogType.Cancel)
    } else {
      onClose()
    }
  }

  const handleBackToForm = () => {
    setOpenedDialog(ProductTestFormDialogType.Form)
    setDiffType(null)
    pendingFormPayloadRef.current = null
  }

  const handleDocumentRemove = (removedDocument: SignedUrl) => {
    setProductTestDocuments((prev) => prev.filter((document) => document.file?.fileId !== removedDocument.file?.fileId))
  }

  const handleVersionUpdate = async (isLotUpdated: boolean) => {
    const payload = pendingFormPayloadRef.current

    if (!payload) {
      handleBackToForm()
      return
    }

    const isSuccess = await updateProductTest(payload, productTest!.productTestId, isLotUpdated)

    if (isSuccess) onSubmit()

    handleBackToForm()
  }

  const submitForm = async (formValues: ProductTestFormValues) => {
    const poIds = linkingOrderId ? productTestPoIds.concat([Number(linkingOrderId)]) : productTestPoIds
    const payload = toProductTestFormPayload(
      formValues,
      productTest,
      productTestDocuments,
      poIds,
      // Indicate whether results field should be sent in the payload
      isCreateMode || "results" in dirtyFields
    )

    if (!isCreateMode) {
      const diffType = compareProductTestValues(productTest, formValues)

      if (diffType) {
        setDiffType(diffType)
        pendingFormPayloadRef.current = payload
        setOpenedDialog(ProductTestFormDialogType.VersionUpdate)
        return
      }
    }

    const isSuccess = isCreateMode
      ? await createProductTest(payload, product.productId)
      : await updateProductTest(payload, productTest.productTestId)

    if (!isSuccess) return

    onSubmit()
  }

  return (
    <>
      {openedDialog === ProductTestFormDialogType.Cancel && (
        <CancelDialog onClose={handleBackToForm} onConfirm={onClose} />
      )}
      {openedDialog === ProductTestFormDialogType.Form && (
        <Dialog open onClose={handleClose} fullWidth maxWidth='md' data-testid='product-test-form-dialog'>
          <DialogHeader title={`${isCreateMode ? "Add" : "Edit"} Test Data`} onClose={handleClose} />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <Box className='flex flex-col gap-y-12'>
                {currentUser && (
                  <ProductTestInfo
                    product={product}
                    shelfLifeMonths={shelfLifeMonths}
                    productTest={productTest}
                    onRemovePo={handlePoRemove}
                    poIds={productTestPoIds}
                  />
                )}
                {isPoLinkMode && productTest ? (
                  <ProductTestCoaInfo productTest={productTest} currentOrderId={linkingOrderId} />
                ) : (
                  <ProductTestCoaForm isCreateMode={isCreateMode} shelfLifeMonths={shelfLifeMonths} />
                )}
                {!isFetchingProductTests && hasExistingProductTestToLinkPo && (
                  <ProductTestLinkPoAlert onProceed={() => onPoLink(existingProductTest, orderId)} />
                )}
                {!isFetchingProductTests && hasExistingProductTest && (
                  <ProductTestExistsAlert lotNumber={lotNumber} batchNumber={batchNumber} />
                )}
                <ProductTestResultsForm
                  associatedTestTemplates={associatedTestTemplates}
                  productTest={productTest}
                  isReadOnly={isPoLinkMode}
                />
                <ProductTestDocumentsForm
                  title='Document Upload'
                  control={control}
                  readOnly={isPoLinkMode}
                  productTestDocuments={productTestDocuments}
                  onDelete={handleDocumentRemove}
                />
              </Box>
            </FormProvider>
          </DialogContent>
          <DialogActions className='px-10 pb-10'>
            <Button appearance='outlined' className='mr-auto' onClick={handleClose} data-testid='cancel-button'>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(submitForm)}
              disabled={!canSubmit && !isPoLinkMode}
              data-testid='submit-button'
            >
              Confirm & Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {openedDialog === ProductTestFormDialogType.VersionUpdate && diffType && (
        <ProductTestVersionUpdateDialog
          diffType={diffType}
          isSubmitting={isSubmitting}
          onClose={handleBackToForm}
          onSubmit={handleVersionUpdate}
        />
      )}
    </>
  )
}
